/**
 * New admin REST API adapter (Swagger spec at /api-docs/).
 *
 * - Base paths: /admin/*, /advertiser/*, /influencer/*
 * - Response envelope: { success: true, data: T, message?: string }
 * - Failure envelope: { success: false, message, errorStack?, errorCode? }
 * - Reuses CSRF + 401 retry behavior from webApi3.ts.
 *
 * The adapter unwraps `.data` on success and converts `{ success: false }`
 * payloads into the existing ApiError shape so downstream error handling
 * keeps working without changes.
 */
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getNewApiBaseUrl } from './config';
import { withRetry, isTransientHttpError } from '@/lib/utils/retry';
import type { ApiError, ErrorDispatcher } from './apiError';

const HEROKU_DOWN_MESSAGE = 'Server status check required. (Heroku error occurring)';

/**
 * "BE is down" detection (drives `useApiDownDetection` / `<ApiDownBanner>`): a running
 * count of CONSECUTIVE 5xx responses across every request made through this client
 * (post/postSilent/get/getSilent/postFile all funnel through the same axios instance).
 * The count resets to 0 the moment any request gets back a 200 or 201 — it is not a
 * time-windowed tally, so a single success anywhere in between clears the streak.
 * The banner shows once the streak reaches `CONSECUTIVE_SERVER_ERROR_THRESHOLD`.
 */
const CONSECUTIVE_SERVER_ERROR_THRESHOLD = 10;

function serializeBody(data: unknown): string | undefined {
  if (data == null) return undefined;
  // HTML response (e.g. Heroku "Application Error" page) — show a fixed summary
  if (typeof data === 'string' && data.trimStart().startsWith('<')) {
    return HEROKU_DOWN_MESSAGE;
  }
  if (typeof data === 'string') return data.slice(0, 10_000);
  try { return JSON.stringify(data, null, 2); } catch { return String(data); }
}

interface SuccessEnvelope<T> {
  success: true;
  data?: T;
  message?: string;
}

interface FailureEnvelope {
  success: false;
  message: string;
  errorStack?: string;
  errorCode?: string;
}

type Envelope<T> = SuccessEnvelope<T> | FailureEnvelope;

interface ApiRequestMeta {
  apiName: string;
  apiPath: string;
  method: string;
}

type UnauthorizedHandler = () => void;

const defaultErrorDispatcher: ErrorDispatcher = (error) => {
  alert(error.message);
};

function readXsrfCookie(): string {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

interface WebApiAdminOptions {
  baseURL?: string;
  errorDispatcher?: ErrorDispatcher;
}

export class WebApiAdmin {
  private client;
  private errorDispatcher: ErrorDispatcher;
  private onUnauthorized?: UnauthorizedHandler;
  // See CONSECUTIVE_SERVER_ERROR_THRESHOLD above for the rule this implements.
  private consecutiveServerErrors = 0;
  private downStateListeners = new Set<(isDown: boolean) => void>();

  constructor(options: WebApiAdminOptions = {}) {
    const basePath = options.baseURL ?? `${getNewApiBaseUrl()}/`;
    this.errorDispatcher = options.errorDispatcher ?? defaultErrorDispatcher;

    this.client = axios.create({
      baseURL: basePath,
      timeout: 120 * 1000,
      withCredentials: true,
      responseType: 'json',
      xsrfCookieName: 'XSRF-TOKEN',
      xsrfHeaderName: 'X-XSRF-TOKEN',
    });

    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = readXsrfCookie();
      if (token && config.headers) {
        config.headers['X-XSRF-TOKEN'] = token;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => {
        if (response.status === 200 || response.status === 201) {
          this.resetConsecutiveServerErrors();
        }
        return response;
      },
      async (error: AxiosError) => {
        if ((error.response?.status ?? 0) >= 500) {
          this.consecutiveServerErrors += 1;
          this.notifyDownState();
        }

        const config = error.config as InternalAxiosRequestConfig & {
          _csrfRetry?: boolean;
          _authRetry?: boolean;
        };

        const responseData = error.response?.data;
        const isCsrfError =
          error.response?.status === 403 &&
          (JSON.stringify(responseData ?? '').includes('CSRF') ||
            String(responseData ?? '').includes('CSRF'));
        if (isCsrfError && !config?._csrfRetry) {
          config._csrfRetry = true;
          try {
            const { ensureCsrfSession, readXsrfCookie: readCookie } = await import('./csrf');
            await ensureCsrfSession(true);
            const newToken = readCookie();
            if (newToken && config.headers) {
              config.headers['X-XSRF-TOKEN'] = newToken;
            }
            return this.client.request(config);
          } catch {
            if (this.onUnauthorized) this.onUnauthorized();
            return Promise.reject(error);
          }
        }

        if (error.response?.status === 401 && !config?._authRetry) {
          config._authRetry = true;
          try {
            const result = await withRetry(
              async () => {
                const { ensureCsrfSession, readXsrfCookie: readCookie } = await import('./csrf');
                await ensureCsrfSession(true);
                const freshToken = readCookie();
                if (freshToken && config.headers) {
                  config.headers['X-XSRF-TOKEN'] = freshToken;
                }
                return this.client.request(config);
              },
              {
                maxRetries: 2,
                baseDelay: 1500,
                maxDelay: 5000,
                shouldRetry: (retryError) => {
                  const axErr = retryError as AxiosError;
                  if (axErr.response?.status === 401) return false;
                  return isTransientHttpError(retryError);
                },
              },
            );
            return result;
          } catch {
            if (this.onUnauthorized) this.onUnauthorized();
          }
        }

        return Promise.reject(error);
      },
    );
  }

  setErrorDispatcher(dispatcher: ErrorDispatcher): void {
    this.errorDispatcher = dispatcher;
  }

  setUnauthorizedHandler(handler: UnauthorizedHandler): void {
    this.onUnauthorized = handler;
  }

  private notifyDownState(): void {
    const isDown = this.consecutiveServerErrors >= CONSECUTIVE_SERVER_ERROR_THRESHOLD;
    this.downStateListeners.forEach((listener) => listener(isDown));
  }

  /** Called on every 200/201 response, and explicitly by the recovery poll in
   *  `useApiDownDetection` after an out-of-band health check succeeds. */
  resetConsecutiveServerErrors(): void {
    if (this.consecutiveServerErrors === 0) return;
    this.consecutiveServerErrors = 0;
    this.notifyDownState();
  }

  /** Subscribe to "is the BE currently considered down" changes (see
   *  CONSECUTIVE_SERVER_ERROR_THRESHOLD above). Invokes `listener` immediately with the
   *  current state, then on every change. Returns an unsubscribe function. */
  subscribeDownState(listener: (isDown: boolean) => void): () => void {
    this.downStateListeners.add(listener);
    listener(this.consecutiveServerErrors >= CONSECUTIVE_SERVER_ERROR_THRESHOLD);
    return () => this.downStateListeners.delete(listener);
  }

  private withApiMeta(
    config: AxiosRequestConfig | undefined,
    meta: ApiRequestMeta,
  ): AxiosRequestConfig & { _apiMeta: ApiRequestMeta } {
    return {
      ...(config ?? {}),
      _apiMeta: meta,
    } as AxiosRequestConfig & { _apiMeta: ApiRequestMeta };
  }

  private normalizePath(path: string): string {
    return path.startsWith('/') ? path.slice(1) : path;
  }

  private unwrap<T>(body: Envelope<T> | undefined, meta: ApiRequestMeta): T {
    if (!body || typeof body !== 'object') {
      throw this.toApiError(
        { message: 'Invalid response body', isProcessed: true },
        meta,
      );
    }
    if (body.success === false) {
      throw this.toApiError(
        {
          message: body.message ?? 'Request failed',
          errors: serializeBody(body),
          errorStack: body.errorStack,
          errorCode: body.errorCode,
          isProcessed: true,
        },
        meta,
      );
    }
    return (body.data as T) ?? (undefined as T);
  }

  private toApiError(partial: Partial<ApiError>, meta: ApiRequestMeta): ApiError {
    return {
      message: partial.message ?? 'Request failed',
      code: partial.code,
      errors: partial.errors,
      errorStack: partial.errorStack,
      errorCode: partial.errorCode,
      apiName: meta.apiName,
      apiPath: meta.apiPath,
      method: meta.method,
      isProcessed: true,
    };
  }

  private processError = (meta: ApiRequestMeta) => (err: unknown): Promise<never> => {
    const error = err as {
      response?: { data?: FailureEnvelope; status?: number; message?: string };
      message?: string;
      isProcessed?: boolean;
    };

    // If we already converted to ApiError in unwrap(), just dispatch+reject.
    if ((err as ApiError)?.isProcessed) {
      this.errorDispatcher(err as ApiError);
      return Promise.reject(err);
    }

    const response = error.response;
    let apiError: ApiError;
    if (response) {
      const body = response.data;
      apiError = this.toApiError(
        {
          message: body?.message ?? response.message ?? 'Request failed',
          code: response.status,
          errors: serializeBody(body),
          errorStack: body?.errorStack,
          errorCode: body?.errorCode,
        },
        meta,
      );
    } else {
      apiError = this.toApiError({ message: error.message ?? String(err) }, meta);
    }
    this.errorDispatcher(apiError);
    return Promise.reject(apiError);
  };

  async post<T = unknown>(
    path: string,
    data: Record<string, unknown> = {},
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const normalized = this.normalizePath(path);
    const meta: ApiRequestMeta = { apiName: normalized, apiPath: normalized, method: 'POST' };
    try {
      const response = await this.client.post<Envelope<T>>(
        normalized,
        data,
        this.withApiMeta(config, meta),
      );
      return this.unwrap<T>(response.data, meta);
    } catch (err) {
      return this.processError(meta)(err);
    }
  }

  /**
   * Same as `post` but skips the global error dispatcher (no alert). The
   * rejected promise still carries the populated `ApiError`, so callers can
   * surface the message inline.
   */
  async postSilent<T = unknown>(
    path: string,
    data: Record<string, unknown> = {},
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const normalized = this.normalizePath(path);
    const meta: ApiRequestMeta = { apiName: normalized, apiPath: normalized, method: 'POST' };
    try {
      const response = await this.client.post<Envelope<T>>(
        normalized,
        data,
        this.withApiMeta(config, meta),
      );
      return this.unwrap<T>(response.data, meta);
    } catch (err) {
      return this.rejectSilently(meta, err);
    }
  }

  private rejectSilently(meta: ApiRequestMeta, err: unknown): Promise<never> {
    if ((err as ApiError)?.isProcessed) {
      return Promise.reject(err);
    }
    const error = err as {
      response?: { data?: FailureEnvelope; status?: number; message?: string };
      message?: string;
    };
    const response = error.response;
    let apiError: ApiError;
    if (response) {
      const body = response.data;
      apiError = this.toApiError(
        {
          message: body?.message ?? response.message ?? 'Request failed',
          code: response.status,
          errors: serializeBody(body),
          errorStack: body?.errorStack,
          errorCode: body?.errorCode,
        },
        meta,
      );
    } else {
      apiError = this.toApiError({ message: error.message ?? String(err) }, meta);
    }
    return Promise.reject(apiError);
  }

  async patch<T = unknown>(
    path: string,
    data: Record<string, unknown> = {},
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const normalized = this.normalizePath(path);
    const meta: ApiRequestMeta = { apiName: normalized, apiPath: normalized, method: 'PATCH' };
    try {
      const response = await this.client.patch<Envelope<T>>(
        normalized,
        data,
        this.withApiMeta(config, meta),
      );
      return this.unwrap<T>(response.data, meta);
    } catch (err) {
      return this.processError(meta)(err);
    }
  }

  async delete<T = unknown>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const normalized = this.normalizePath(path);
    const meta: ApiRequestMeta = { apiName: normalized, apiPath: normalized, method: 'DELETE' };
    try {
      const response = await this.client.delete<Envelope<T>>(
        normalized,
        this.withApiMeta(config, meta),
      );
      return this.unwrap<T>(response.data, meta);
    } catch (err) {
      return this.processError(meta)(err);
    }
  }

  async get<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T> {
    const normalized = this.normalizePath(path);
    const meta: ApiRequestMeta = { apiName: normalized, apiPath: normalized, method: 'GET' };
    try {
      const response = await this.client.get<Envelope<T>>(
        normalized,
        this.withApiMeta({ params }, meta),
      );
      return this.unwrap<T>(response.data, meta);
    } catch (err) {
      return this.processError(meta)(err);
    }
  }

  /**
   * Same as `get` but skips the global error dispatcher (no alert). Used for
   * background polls (e.g. notification load on every socket ring) where a
   * transient failure must not surface a blocking alert. The rejected promise
   * still carries the populated `ApiError`.
   */
  async getSilent<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T> {
    const normalized = this.normalizePath(path);
    const meta: ApiRequestMeta = { apiName: normalized, apiPath: normalized, method: 'GET' };
    try {
      const response = await this.client.get<Envelope<T>>(
        normalized,
        this.withApiMeta({ params }, meta),
      );
      return this.unwrap<T>(response.data, meta);
    } catch (err) {
      return this.rejectSilently(meta, err);
    }
  }

  async postFile<T = unknown>(path: string, data: { file: File }): Promise<T> {
    const normalized = this.normalizePath(path);
    const meta: ApiRequestMeta = { apiName: normalized, apiPath: normalized, method: 'POST' };
    const formData = new FormData();
    formData.append('file', data.file);
    try {
      const response = await this.client.post<Envelope<T>>(
        normalized,
        formData,
        this.withApiMeta(undefined, meta),
      );
      return this.unwrap<T>(response.data, meta);
    } catch (err) {
      return this.processError(meta)(err);
    }
  }
}

const webApiAdmin = new WebApiAdmin();

export default webApiAdmin;
